// procedureBrief — deterministic procedure briefing generator.
//
// Takes a CIFP Procedure object (already fetched from /api/procedures)
// and a selected transition ident and produces a structured
// ProcedureBrief object for display in ProcedureBriefPanel.
//
// KEY DESIGN RULE: Never hallucinate data. Everything in ProcedureBrief
// comes directly from CIFP leg fields. If a field is unknown (null in
// CIFP), we say "unknown" or omit it — never invent a value.

import type { Procedure, ProcedureLeg, Transition, RouteType } from './cifp/cifp';
import { parseIapIdent } from './procedurePlateMatch';

// ── Types ────────────────────────────────────────────────────────────────

export interface BriefLeg {
  /** Fix ident (e.g. 'LAUSI', 'RW14', '') */
  fixIdent: string;
  /** CIFP path-and-terminator code */
  legType: string;
  /** Magnetic course or heading (degrees), null when not published */
  courseMag: number | null;
  /** Altitude constraint formatted as a human string, e.g. '≥4000', '≤2000', '1800–3000', '4000' */
  altConstraint: string | null;
  /** Speed constraint in kt, null when not published */
  speedKt: number | null;
  /** True when this is a missed-approach leg */
  isMissed: boolean;
  /** Fix role flags */
  isIaf: boolean;
  isFaf: boolean;
  isMap: boolean;
  isIntermediateFix: boolean;
  isStepdownFix: boolean;
  isHoldingPattern: boolean;
  isFlyOver: boolean;
  /** Holding details (only on HM/HF/HA legs) */
  holdInboundCourse: number | null;
  holdLeg: ProcedureLeg['holdLeg'];
  holdTurnDir: 'L' | 'R' | null;
}

export interface ProcedureBrief {
  /** Airport ICAO */
  airportIcao: string;
  /** Procedure ident (e.g. 'I14', 'CIITY3', 'HAWKZ8') */
  procIdent: string;
  /** Human-readable name: parsed from ident (e.g. 'ILS RWY 14', 'CIITY THREE', 'HAWKZ EIGHT') */
  displayName: string;
  /** Route type */
  routeType: RouteType;
  /** Runway if applicable (e.g. '14', '29R') */
  runway: string | null;
  /** Selected transition ident */
  transitionIdent: string;
  /** All available transition names (for display) */
  allTransitions: string[];

  // ── Key values ───────────────────────────────────────────────────────

  /** Inbound course (degrees magnetic) for approach / SID initial / STAR final */
  inboundCourse: number | null;
  /** Top altitude in procedure (highest at-or-above constraint) — ft MSL */
  topAltitudeFt: number | null;
  /** Top altitude formatted string (e.g. '10000') */
  topAltitudeStr: string | null;
  /** DA or MDA (lowest published altitude in final approach segment) */
  daStr: string | null;
  /** FAF altitude string */
  fafAltStr: string | null;
  /** True when a procedure turn (PI) or HILPT is in the leg list */
  hasProcedureTurn: boolean;
  /** True when a hold-in-lieu-of-PT is present */
  hasHilpt: boolean;
  /** Magnetic variation note — not computed, always null (placeholder for future) */
  magVarNote: string | null;

  // ── Key fixes ────────────────────────────────────────────────────────

  /** Initial approach fix idents (legs with isIaf=true) */
  iafFixes: string[];
  /** IF ident (legs with isIntermediateFix=true) */
  ifFix: string | null;
  /** FAF ident */
  fafFix: string | null;
  /** MAP ident */
  mapFix: string | null;
  /** MAHP ident — first leg after MAP in missed segment that has a fix */
  mahpFix: string | null;
  /** Transition entry fixes (STAR/SID only) */
  transitionEntryFixes: string[];
  /** Top altitude fix (SID: fix where you reach max alt) */
  topAltFix: string | null;

  // ── Leg table ────────────────────────────────────────────────────────

  /** Ordered legs to display in the table. Includes combined transition + common. */
  legs: BriefLeg[];
  /** Approach legs only (before missed) */
  approachLegs: BriefLeg[];
  /** Missed approach legs */
  missedLegs: BriefLeg[];

  // ── Speech ───────────────────────────────────────────────────────────

  /** Verbal brief text (deterministic template). Read aloud with SpeechSynthesis. */
  speechText: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatAlt(alt: string | null): number | null {
  if (!alt) return null;
  const clean = alt.replace(/^FL/, '').replace(/[^0-9]/g, '');
  const n = Number(clean);
  return Number.isFinite(n) && n > 0 ? (alt.startsWith('FL') ? n * 100 : n) : null;
}

function formatAltConstraint(leg: ProcedureLeg): string | null {
  const a1 = leg.altitude1;
  const a2 = leg.altitude2;
  const c = leg.altitudeConstraint;
  if (!a1 || !c) return null;

  const fmt = (s: string) => s.replace(/^FL/, 'FL').trim();
  switch (c) {
    case '+':           return `≥${fmt(a1)}`;
    case '-':           return `≤${fmt(a1)}`;
    case 'B':           return a2 ? `${fmt(a2)}–${fmt(a1)}` : `≥${fmt(a1)}`;
    case 'at':          return fmt(a1);
    case 'recommended': return `${fmt(a1)}*`;
    default:            return fmt(a1);
  }
}

/** Spell out a number suffix like '3' → 'THREE', '8' → 'EIGHT'. */
const NUMBER_WORDS: Record<string, string> = {
  '1': 'ONE', '2': 'TWO', '3': 'THREE', '4': 'FOUR', '5': 'FIVE',
  '6': 'SIX', '7': 'SEVEN', '8': 'EIGHT', '9': 'NINE',
};

function sidStarDisplayName(procIdent: string): string {
  // e.g. 'CIITY3' → 'CIITY THREE', 'HAWKZ8' → 'HAWKZ EIGHT', 'ALWYS3' → 'ALWYS THREE'
  const match = procIdent.match(/^([A-Z]+)(\d+)$/);
  if (!match) return procIdent;
  const [, root, num] = match;
  const word = NUMBER_WORDS[num] ?? num;
  return `${root} ${word}`;
}

function iapDisplayName(procIdent: string): string {
  const parsed = parseIapIdent(procIdent);
  if (!parsed) return procIdent;
  const variant = parsed.variant ? ` ${parsed.variant}` : '';
  return `${parsed.approachType}${variant} RWY ${parsed.runway}`;
}

/** Get legs for a transition by ident. Null if not found. */
function findTransitionLegs(proc: Procedure, transIdent: string): ProcedureLeg[] | null {
  const t = proc.transitions.find((tr) => tr.ident === transIdent);
  return t ? t.legs : null;
}

/** Find the common-segment transition for a procedure. */
function findCommonTransition(proc: Procedure): Transition | null {
  return proc.transitions.find((tr) => tr.kind === 'common') ?? null;
}

/**
 * Merge transition + common segment legs in the right order.
 *
 * SID:  runway transition legs → common segment legs
 * STAR/IAP: enroute/named transition legs → common segment legs
 *           (or: common segment legs only when transIdent is the common)
 *
 * Deduplication: if the last leg of the first set has the same fixIdent
 * as the first leg of the second set (and it's an IF/DF leg), the duplicate
 * is removed from the second set.
 */
function combineLegs(proc: Procedure, transIdent: string): ProcedureLeg[] {
  const common = findCommonTransition(proc);
  const selectedTrans = proc.transitions.find((tr) => tr.ident === transIdent) ?? null;

  // If the selected transition IS the common segment, just return common legs.
  if (selectedTrans?.kind === 'common') {
    return selectedTrans.legs.slice();
  }

  const primaryLegs  = selectedTrans?.legs ?? [];
  const commonLegs   = common?.legs ?? [];

  // Deduplicate junction: if the last primary fix == first common fix
  // and the common leg is an IF/TF/DF, drop the duplicate.
  let secondary = commonLegs;
  if (primaryLegs.length > 0 && secondary.length > 0) {
    const lastPrimary = primaryLegs[primaryLegs.length - 1];
    const firstCommon = secondary[0];
    if (
      lastPrimary.fixIdent
      && lastPrimary.fixIdent === firstCommon.fixIdent
      && (firstCommon.legType === 'IF' || firstCommon.legType === 'TF' || firstCommon.legType === 'DF')
    ) {
      secondary = secondary.slice(1);
    }
  }

  return [...primaryLegs, ...secondary];
}

/** Convert a raw ProcedureLeg to a BriefLeg. */
function toBriefLeg(leg: ProcedureLeg): BriefLeg {
  return {
    fixIdent: leg.fixIdent,
    legType: leg.legType,
    courseMag: leg.magCourse ?? leg.holdInboundCourse ?? null,
    altConstraint: formatAltConstraint(leg),
    speedKt: leg.speedLimit,
    isMissed: leg.isMissedApproach,
    isIaf: leg.isIaf,
    isFaf: leg.isFaf,
    isMap: leg.isMap,
    isIntermediateFix: leg.isIntermediateFix,
    isStepdownFix: leg.isStepdownFix,
    isHoldingPattern: leg.isHoldingPattern,
    isFlyOver: leg.isFlyOver,
    holdInboundCourse: leg.holdInboundCourse,
    holdLeg: leg.holdLeg,
    holdTurnDir: leg.turnDirection,
  };
}

// ── Main generator ───────────────────────────────────────────────────────

export function generateBrief(proc: Procedure, transIdent: string): ProcedureBrief {
  const rawLegs = combineLegs(proc, transIdent);
  const legs = rawLegs.map(toBriefLeg);

  const approachLegs = legs.filter((l) => !l.isMissed);
  const missedLegs   = legs.filter((l) => l.isMissed);

  // Key fix identification
  const iafFixes = legs.filter((l) => l.isIaf && l.fixIdent).map((l) => l.fixIdent);
  const ifLeg    = legs.find((l) => l.isIntermediateFix && l.fixIdent);
  const fafLeg   = legs.find((l) => l.isFaf && l.fixIdent);
  const mapLeg   = legs.find((l) => l.isMap && l.fixIdent);

  // MAHP: first leg after MAP that has a named fix in the missed segment
  const mapIdx   = legs.findIndex((l) => l.isMap);
  const mahpLeg  = mapIdx >= 0
    ? legs.slice(mapIdx + 1).find((l) => l.fixIdent && !l.isMap)
    : null;

  // Procedure turn / HILPT detection
  const hasProcedureTurn = legs.some((l) => l.legType === 'PI');
  const hasHilpt         = legs.some((l) => l.isHoldingPattern && approachLegs.some((a) => a === l));

  // Top altitude: highest altitude value in non-missed legs
  let topAltitudeFt: number | null = null;
  let topAltitudeStr: string | null = null;
  let topAltFix: string | null = null;
  for (const leg of approachLegs) {
    const raw = rawLegs[legs.indexOf(leg)];
    const ft  = formatAlt(raw?.altitude1 ?? null);
    if (ft !== null && (topAltitudeFt === null || ft > topAltitudeFt)) {
      topAltitudeFt = ft;
      topAltitudeStr = raw?.altitude1 ?? null;
      topAltFix = raw?.fixIdent || null;
    }
  }

  // DA / MDA: lowest altitude in final approach segment (between FAF and MAP)
  let daStr: string | null = null;
  const fafIdx = rawLegs.findIndex((l) => l.isFaf);
  const mapIdxRaw = rawLegs.findIndex((l) => l.isMap);
  if (fafIdx >= 0) {
    const finalLegs = fafIdx >= 0 && mapIdxRaw > fafIdx
      ? rawLegs.slice(fafIdx, mapIdxRaw + 1)
      : rawLegs.slice(fafIdx);
    let lowestFt: number | null = null;
    for (const leg of finalLegs) {
      const ft = formatAlt(leg.altitude1 ?? null);
      if (ft !== null && (lowestFt === null || ft < lowestFt)) {
        lowestFt = ft;
        daStr = leg.altitude1;
      }
    }
  }

  // FAF altitude
  const fafAltStr = fafLeg
    ? (rawLegs.find((l) => l.isFaf)?.altitude1 ?? null)
    : null;

  // Inbound course: for IAP, it's the FAF leg's magCourse.
  // For SID, it's the first leg's magCourse.
  // For STAR, it's the last common leg's magCourse.
  let inboundCourse: number | null = null;
  if (proc.routeType === 'IAP') {
    inboundCourse = rawLegs.find((l) => l.isFaf)?.magCourse ?? null;
    if (!inboundCourse) {
      // Fall back: last approach leg before MAP with a course
      const lastApproachWithCourse = [...approachLegs].reverse().find((l) => l.courseMag !== null);
      inboundCourse = lastApproachWithCourse?.courseMag ?? null;
    }
  } else if (proc.routeType === 'SID') {
    inboundCourse = rawLegs[0]?.magCourse ?? null;
  } else {
    // STAR: last leg before runway transition
    inboundCourse = [...rawLegs].reverse().find((l) => l.magCourse !== null)?.magCourse ?? null;
  }

  // Transition entry fixes (STAR / SID)
  const transitionEntryFixes: string[] = [];
  if (proc.routeType === 'STAR' || proc.routeType === 'SID') {
    const firstLeg = rawLegs[0];
    if (firstLeg?.fixIdent) transitionEntryFixes.push(firstLeg.fixIdent);
  }

  // Display name
  let displayName: string;
  let runway: string | null = null;
  if (proc.routeType === 'IAP') {
    displayName = iapDisplayName(proc.procIdent);
    runway = parseIapIdent(proc.procIdent)?.runway ?? null;
  } else {
    displayName = sidStarDisplayName(proc.procIdent);
  }

  // All transition names for display
  const allTransitions = proc.transitions.map((tr) => tr.ident).filter(Boolean);

  // Build speech text
  const speechText = buildSpeechText({
    proc, displayName, runway, legs, approachLegs, missedLegs,
    iafFixes, fafLeg, mapLeg, mahpLeg, fafAltStr, daStr,
    inboundCourse, topAltitudeStr, transIdent, topAltFix,
    hasProcedureTurn, hasHilpt,
  });

  return {
    airportIcao: proc.airportIcao,
    procIdent: proc.procIdent,
    displayName,
    routeType: proc.routeType,
    runway,
    transitionIdent: transIdent,
    allTransitions,
    inboundCourse,
    topAltitudeFt,
    topAltitudeStr,
    daStr,
    fafAltStr,
    hasProcedureTurn,
    hasHilpt,
    magVarNote: null,
    iafFixes,
    ifFix: ifLeg?.fixIdent ?? null,
    fafFix: fafLeg?.fixIdent ?? null,
    mapFix: mapLeg?.fixIdent ?? null,
    mahpFix: mahpLeg?.fixIdent ?? null,
    transitionEntryFixes,
    topAltFix,
    legs,
    approachLegs,
    missedLegs,
    speechText,
  };
}

// ── Speech generator ─────────────────────────────────────────────────────

interface SpeechCtx {
  proc: Procedure;
  displayName: string;
  runway: string | null;
  legs: BriefLeg[];
  approachLegs: BriefLeg[];
  missedLegs: BriefLeg[];
  iafFixes: string[];
  fafLeg: BriefLeg | undefined | null;
  mapLeg: BriefLeg | undefined | null;
  mahpLeg: BriefLeg | undefined | null;
  fafAltStr: string | null;
  daStr: string | null;
  inboundCourse: number | null;
  topAltitudeStr: string | null;
  transIdent: string;
  topAltFix: string | null;
  hasProcedureTurn: boolean;
  hasHilpt: boolean;
}

function buildSpeechText(ctx: SpeechCtx): string {
  const { proc, displayName, iafFixes, fafLeg, mapLeg, mahpLeg,
          fafAltStr, daStr, inboundCourse, topAltitudeStr,
          transIdent, missedLegs, hasProcedureTurn, hasHilpt } = ctx;

  const airport = proc.airportIcao;
  const parts: string[] = [];

  if (proc.routeType === 'IAP') {
    parts.push(`${displayName} at ${airport}.`);
    if (iafFixes.length > 0) {
      parts.push(`Initial approach fix${iafFixes.length > 1 ? 'es' : ''}: ${iafFixes.join(', ')}.`);
    }
    if (inboundCourse !== null) {
      parts.push(`Inbound course ${Math.round(inboundCourse)} degrees.`);
    }
    if (fafLeg?.fixIdent) {
      const fafAlt = fafAltStr ? `, ${fafAltStr} feet` : '';
      parts.push(`Final approach fix: ${fafLeg.fixIdent}${fafAlt}.`);
    }
    if (hasProcedureTurn) parts.push('Procedure turn required.');
    if (hasHilpt) parts.push('Hold in lieu of procedure turn.');
    if (daStr) {
      parts.push(`Decision altitude or minimum descent altitude: ${daStr} feet.`);
    }
    if (mapLeg?.fixIdent) {
      parts.push(`Missed approach point: ${mapLeg.fixIdent}.`);
    }
    // Missed approach summary: first few named fixes
    const missedFixes = missedLegs.filter((l) => l.fixIdent).map((l) => l.fixIdent);
    if (missedFixes.length > 0) {
      const missedCourse = missedLegs[0]?.courseMag;
      if (missedCourse !== null && missedCourse !== undefined) {
        parts.push(`Missed approach: climb heading ${Math.round(missedCourse)}.`);
      }
      parts.push(`Then via ${missedFixes.slice(0, 3).join(', ')}.`);
    }

  } else if (proc.routeType === 'SID') {
    parts.push(`${displayName} Departure from ${airport}.`);
    const transClean = transIdent.replace(/^\d/, '').replace(/^RW/, 'Runway ');
    if (transClean && transClean !== 'Runway ') {
      parts.push(`Runway transition: ${transClean}.`);
    }
    if (inboundCourse !== null) {
      parts.push(`Initial heading ${Math.round(inboundCourse)} degrees.`);
    }
    if (topAltitudeStr) {
      parts.push(`Top altitude ${topAltitudeStr} feet.`);
    }
    const namedFixes = ctx.legs.filter((l) => l.fixIdent && !l.isMap).map((l) => l.fixIdent).slice(0, 5);
    if (namedFixes.length > 0) {
      parts.push(`Route: ${namedFixes.join(', ')}.`);
    }

  } else {
    // STAR
    parts.push(`${displayName} Arrival to ${airport}.`);
    if (transIdent) {
      const transClean = transIdent.replace(/^\d/, '');
      parts.push(`Entry transition: ${transClean}.`);
    }
    if (topAltitudeStr) {
      parts.push(`Top altitude ${topAltitudeStr} feet.`);
    }
    // Key altitude waypoints from the approach legs
    const altFixes = ctx.approachLegs
      .filter((l) => l.fixIdent && l.altConstraint)
      .slice(0, 4);
    for (const fix of altFixes) {
      parts.push(`${fix.fixIdent} at ${fix.altConstraint} feet.`);
    }
  }

  return parts.join(' ');
}
